/**
 * fastapi-url.ts — single source of truth for the FastAPI base URL.
 *
 * Why this exists (#237 + #239):
 *   The Songs Library, Song Workspace, and Song Detail routes each
 *   used to declare their own DEFAULT_API. Initial production fallback
 *   was http://localhost:8000 → Safari mixed-content blocked → "Load
 *   failed" on fresh browsers (#237). After fixing that to the direct
 *   Fly URL, fresh browsers then hit HTTP 401 because they had no
 *   X-Ojaswati-Key in localStorage.
 *
 *   The right answer (#239) is to never expose the Fly key in the
 *   browser at all. In production we now default to the same-origin
 *   /api/fly proxy (functions/api/fly/[[path]].ts), which:
 *     1. Verifies the user's Cloudflare session cookie
 *     2. Attaches X-Ojaswati-Key from the OJASWATI_API_KEY
 *        Cloudflare secret
 *     3. Forwards to https://ojaswati-backend.fly.dev/<path>
 *
 *   Net effect: new browsers just need to sign in to Cloudflare. The
 *   Fly key never touches localStorage.
 *
 * Override precedence (high → low):
 *   1. localStorage["fastapi_url"]      — power-user manual override
 *                                         (e.g. point at local dev Fly)
 *   2. import.meta.env.VITE_FASTAPI_URL — bake-time build secret
 *   3. /api/fly (same-origin proxy)     — production default in browsers
 *   4. http://localhost:8000            — dev fallback when running on localhost
 */

/* Same-origin Cloudflare Pages Functions proxy. Empty-string base
 * means each fetch resolves the path against window.location.origin.
 * Path arithmetic in callers (e.g. `${apiUrl}/youtube-queue/list`)
 * still works because the resulting URL is "/api/fly/youtube-queue/list". */
const PROD_FALLBACK = "/api/fly";
const DEV_FALLBACK = "http://localhost:8000";

function isLocalHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return (
    host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0"
  );
}

export function fastapiBase(): string {
  // 1. Manual override (Settings → "API URL" input writes this).
  try {
    if (typeof window !== "undefined") {
      const ls = window.localStorage.getItem("fastapi_url");
      if (ls && ls.trim()) return ls.replace(/\/$/, "");
    }
  } catch {
    /* localStorage blocked (Safari private mode, etc.) — fall through */
  }
  // 2. Bake-time env var.
  const envUrl = (import.meta.env.VITE_FASTAPI_URL as string | undefined)?.trim();
  if (envUrl) return envUrl.replace(/\/$/, "");
  // 3 / 4. Smart fallback by current host.
  return isLocalHost() ? DEV_FALLBACK : PROD_FALLBACK;
}

/** Per-call API key stored under `ojaswati_api_key`. Empty string when
 * unset (the Fly backend treats it as anonymous; production endpoints
 * gated by `require_api_key` will return 401). */
export const API_KEY_STORAGE = "ojaswati_api_key";
export const API_KEY_HEADER = "X-Ojaswati-Key";

export function apiHeaders(extra?: Record<string, string>): Headers {
  const h = new Headers(extra);
  try {
    if (typeof window !== "undefined") {
      const k = window.localStorage.getItem(API_KEY_STORAGE);
      if (k) h.set(API_KEY_HEADER, k);
    }
  } catch {
    /* localStorage blocked */
  }
  return h;
}
